import { AskStreamEventId } from '../ask'

type ThreadEvent = { id: string; event?: unknown }
type WithRelativeId = { relativeId: string }

const LEDGER_EVENT_IDS: ReadonlySet<string> = new Set([
  AskStreamEventId.SuperSources,
  AskStreamEventId.SuperArtifacts,
  AskStreamEventId.SuperSourceExplanation,
])

const payloadOf = (event: ThreadEvent): Record<string, unknown> =>
  (event.event ?? {}) as Record<string, unknown>

const RELATIVE_ID_TOKEN = /[A-Za-z0-9]+-\d+/g

/** Every source or artifact id (`5L1T3-4`, `4RT1F4CT-2`) anywhere in a value. */
export const relativeIdsIn = (value: unknown): Set<string> =>
  new Set(JSON.stringify(value).match(RELATIVE_ID_TOKEN) ?? [])

/**
 * Keeps the artifacts, sources and source explanations that the rest of the
 * thread (or `referenced`) still cites, and the sources a kept artifact cites.
 * A pruned event is dropped, or emptied in place with `keepEvents` so the log
 * keeps its length: a thread's revision counts its events.
 */
export function withoutUncitedLedgerEntries<T extends ThreadEvent>(
  events: readonly T[],
  {
    referenced,
    keepEvents = false,
  }: { referenced?: unknown; keepEvents?: boolean } = {}
): T[] {
  const keepCited = (
    event: T,
    key: 'artifacts' | 'sources',
    cited: ReadonlySet<string>
  ): T[] => {
    const payload = payloadOf(event)
    const entries = payload[key] as WithRelativeId[]
    const kept = entries.filter(({ relativeId }) => cited.has(relativeId))
    if (kept.length === 0 && !keepEvents) return []
    return [{ ...event, event: { ...payload, [key]: kept } }]
  }

  const cited = relativeIdsIn([
    referenced,
    events.filter(({ id }) => !LEDGER_EVENT_IDS.has(id)),
  ])
  const withCitedArtifacts = events.flatMap((event) =>
    event.id === AskStreamEventId.SuperArtifacts
      ? keepCited(event, 'artifacts', cited)
      : [event]
  )
  const citedWithArtifacts = new Set([
    ...cited,
    ...relativeIdsIn(
      withCitedArtifacts.filter(
        ({ id }) => id === AskStreamEventId.SuperArtifacts
      )
    ),
  ])

  return withCitedArtifacts.flatMap((event): T[] => {
    if (event.id === AskStreamEventId.SuperSources)
      return keepCited(event, 'sources', citedWithArtifacts)
    if (event.id !== AskStreamEventId.SuperSourceExplanation) return [event]
    const { relativeId } = payloadOf(event) as WithRelativeId
    if (citedWithArtifacts.has(relativeId)) return [event]
    return keepEvents
      ? [{ ...event, event: { relativeId, explanation: '' } }]
      : []
  })
}
